import React, { useEffect, useMemo, useState } from 'react'
import PropTypes from 'prop-types'
import { BiDownArrow, BiLeftArrow, BiRightArrow, BiUpArrow } from 'react-icons/bi'
import { ChevronDown, ChevronRight } from 'lucide-react'


export default function CommonTable({
  columns,
  data,
  initialSort = null,
  searchable = true,
  pagination = true,
  initialPageSize = 10,
  pageSizeOptions = [5, 10, 25, 50],
  rowKey = 'id',
  onRowClick = null,
  rowActions = null,
  expandedRowRender = null,
  nextPageUrl = null,
  prevPageUrl = null,
  onPageChange = null,
  totalCount = null,
  page = null,
}) {
  const [query, setQuery] = useState('')
  const [sortConfig, setSortConfig] = useState(initialSort)
  const [pageSize, setPageSize] = useState(initialPageSize)
  const [pageIndex, setPageIndex] = useState(0)
  const [expandedRows, setExpandedRows] = useState([])
  const [expandedRowKey, setExpandedRowKey] = useState(null)

  const searchableColumns = useMemo(
    () => columns.filter((c) => c.search !== false).map((c) => c.key),
    [columns]
  )

  const filtered = useMemo(() => {
    if (!query) return data
    const q = query.toLowerCase()
    return data.filter((row) =>
      searchableColumns.some((col) => {
        const v = row[col]
        if (v === null || v === undefined) return false
        return String(v).toLowerCase().includes(q)
      })
    )
  }, [data, query, searchableColumns])

  const sorted = useMemo(() => {
    if (!sortConfig || !sortConfig.key) return filtered
    const { key, direction } = sortConfig
    const dir = direction === 'desc' ? -1 : 1
    return [...filtered].sort((a, b) => {
      const va = a[key]
      const vb = b[key]
      if (va == null && vb == null) return 0
      if (va == null) return -1 * dir
      if (vb == null) return 1 * dir
      if (typeof va === 'number' && typeof vb === 'number') {
        return (va - vb) * dir
      }
      return String(va).localeCompare(String(vb)) * dir
    })
  }, [filtered, sortConfig])

  const pageCount = Math.max(1, Math.ceil(sorted?.length / pageSize))
  const paged = useMemo(() => {
    if (!pagination) return Array.isArray(sorted) ? sorted : []

    const start = pageIndex * pageSize
    const list = Array.isArray(sorted) ? sorted : []
    return list.slice(start, start + pageSize)
  }, [sorted, pageIndex, pageSize, pagination])


  // function toggleExpand(key) {
  //   setExpandedRows((prev) => {
  //     const exists = prev.includes(key)
  //     if (exists) return prev.filter((k) => k !== key)
  //     return [...prev, key]
  //   })
  // }
  function toggleExpand(key) {
    setExpandedRowKey((prev) => (prev === key ? null : key))
  }

  function handleSort(col) {
    if (!col.sortable) return
    if (!sortConfig || sortConfig.key !== col.key) {
      setSortConfig({ key: col.key, direction: 'asc' })
      return
    }
    setSortConfig((s) => ({ key: col.key, direction: s.direction === 'asc' ? 'desc' : 'asc' }))
  }

  function gotoPage(i) {
    const next = Math.max(0, Math.min(pageCount - 1, i))
    setPageIndex(next)
  }

  useEffect(() => {
    setExpandedRowKey(null);
  }, [data]);

  // Calculate current page (1-based) for display and range
  const currentPage = page ?? (pageIndex + 1);
  const startIdx = sorted.length === 0 ? 0 : (currentPage - 1) * pageSize + 1;
  const endIdx = Math.min(currentPage * pageSize, totalCount ?? sorted.length);
  return (
    <div className="w-full bg-white rounded-lg shadow-sm overflow-hidden">
      {/* <div className="flex items-center justify-between p-4 border-b">
        <div className="text-lg font-medium">Table</div>
        {searchable && (
          <div className="w-64">
            <input
              value={query}
              onChange={(e) => { setQuery(e.target.value); setPageIndex(0) }}
              placeholder="Search..."
              className="w-full px-3 py-2 border rounded-md text-sm focus:outline-none focus:ring"
            />
          </div>
        )}
      </div> */}

      <div
        className="overflow-x-auto"
        style={paged.length >= 0 && paged.length <= 4 ? { minHeight: "300px" } : {}}
      >
        <table className="min-w-full text-sm text-gray-700">
          <thead className="bg-gray-50 border-b">
            <tr>
              {columns.map((col) => (
                <th
                  key={col.key}
                  onClick={() => handleSort(col)}
                  className={`px-4 py-4 text-left align-top text-gray-500 whitespace-nowrap  ${col.sortable ? 'cursor-pointer select-none' : ''}`}
                >
                  <div className="flex items-center gap-2">
                    <span>{col.label}</span>
                    {col.sortable && sortConfig && sortConfig.key === col.key && (
                      <span className="text-xs text-gray-500">{sortConfig.direction === 'asc' ? '▲' : '▼'}</span>
                    )}
                  </div>
                </th>
              ))}
              {rowActions && <th className="px-4 py-3 text-left">Actions</th>}
              {expandedRowRender && <th className="px-2" />}
            </tr>
          </thead>
          <tbody>
            {paged.map((row, rIdx) => {
              const key = String(row[rowKey])
              const isExpanded = expandedRowKey === key

              return (
                <React.Fragment key={key}>
                  <tr
                    onClick={(e) => {
                      if (e.target.closest('[data-expand-btn]')) return;
                      onRowClick && onRowClick(row);
                    }}
                    className="odd:bg-white even:bg-gray-50 hover:bg-gray-100"
                  >

                    {columns.map((col) => (
                      <td key={col.key} className="px-4 py-4 align-top text-gray-500  text-mute font-medium ">
                        {col.render ? col.render(row[col.key], row) : row[col.key]}
                      </td>
                    ))}

                    {rowActions && <td className="px-4 py-4 align-top">{rowActions(row)}</td>}

                    {expandedRowRender && (
                      <td className="px-4 py-4">
                        <button
                          data-expand-btn
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            toggleExpand(key);
                          }}
                          className="w-8 h-8 rounded-full flex items-center justify-center"
                        >
                          {isExpanded ? <ChevronDown /> : <ChevronRight />}
                        </button>
                      </td>
                    )}
                  </tr>

                  {isExpanded && (
                    <tr className='ps-2'>
                      <td
                        colSpan={columns.length + (rowActions ? 1 : 0) + 1}
                        className="px-4 py-8  bg-yellow-100 text-yellow-600 border-l-2 border-yellow-500 font-medium"
                        style={{ minHeight: '100px', height: "100px" }}
                      >
                        {expandedRowRender(row)}
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              )
            })}
            {paged.length === 0 && (
              <tr>
                <td colSpan={columns.length + (rowActions ? 1 : 0) + (expandedRowRender ? 1 : 0)} className="px-4 py-6 text-center text-gray-500">
                  No records found
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {pagination && (
        <div className="flex items-center justify-between px-4 py-3 border-t border-gray-100">
          <div className="text-sm text-gray-500">
            Showing {startIdx} to {endIdx} of {totalCount ?? sorted.length} entries
          </div>

          <div className="flex items-center gap-1">
            <button
              onClick={() => {
                if (onPageChange && prevPageUrl) onPageChange(prevPageUrl);
              }}
              disabled={!prevPageUrl}
              className="flex items-center justify-center w-8 h-8 rounded border border-gray-200 text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed" 
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" /></svg>
            </button>
            <span className="flex items-center justify-center w-8 h-8 rounded bg-[#00bcd4] text-white text-sm font-medium">
              {currentPage}
            </span>
            <button
              onClick={() => {
                if (onPageChange && nextPageUrl) onPageChange(nextPageUrl);
              }}
              disabled={!nextPageUrl}
              className="flex items-center justify-center w-8 h-8 rounded border border-gray-200 text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" /></svg>
            </button>
          </div>
        </div>
      )}
    </div>
  )
}

CommonTable.propTypes = {
  columns: PropTypes.array.isRequired,
  data: PropTypes.array.isRequired,
  initialSort: PropTypes.object,
  searchable: PropTypes.bool,
  pagination: PropTypes.bool,
  initialPageSize: PropTypes.number,
  pageSizeOptions: PropTypes.array,
  rowKey: PropTypes.string,
  onRowClick: PropTypes.func,
  rowActions: PropTypes.func,
  expandedRowRender: PropTypes.func,
  nextPageUrl: PropTypes.string,
  prevPageUrl: PropTypes.string,
  onPageChange: PropTypes.func,
  totalCount: PropTypes.number,
}
